import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from 'app/modules-services/auth-services/authentication.service';
import { Subject, defer, of } from 'rxjs';
import { catchError, delay, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';

import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';

import { JwtHelperService } from '@auth0/angular-jwt';
import { DecodedJWTToken } from './modules-services/auth-services/authentication.models';
import { PermissionsService } from './modules-services/permissions.service';
import { NavigationService } from './navigation/navigation.service';
import { ForfettappApiClient } from './shared/forfettapp-api-client';
import { GlobalLoaderService } from './shared/global-loader-folder/global-loader.service';
import { NotifyUserService } from './shared/notify-user.service';
// import itMessages from "devextreme/localization/messages/it.json";
import { SelezioneForfettarioService } from '@shared/selezione-forfettario-folder/selezione-forfettario.service';
import { SignalrCoreService } from '@shared/signalr-core-service';
import config from 'devextreme/core/config';
import { loadMessages, locale } from 'devextreme/localization';
import itMessages from 'devextreme/localization/messages/it.json';
import { ForfettarioListItem } from './main/forfettario-folder/forfettario.models';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    private tokenExpiryTimer: ReturnType<typeof setTimeout>;

    // Re-entrancy guard: prevents multiple concurrent startup silent-refresh attempts
    // while authData$ re-emits during the refresh.
    private isHandlingStartupRefresh = false;

    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private authService: AuthenticationService,
        private navigationService: NavigationService,
        private _platform: Platform,
        private apiClient: ForfettappApiClient,
        private selezioneForfettarioService: SelezioneForfettarioService,
        private notifyUserService: NotifyUserService,
        protected signalRService: SignalrCoreService,
        private permissionsService: PermissionsService,
        protected fuseSplashScreenService: FuseSplashScreenService, // NON RIMUOVERE ALTRIMENTI LA SPLASH NON SI CHIUDE
        private gloabalLoader: GlobalLoaderService
    ) {
        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        // this.signaRService.startConnection();

        loadMessages(itMessages);

        locale(navigator.language);
        config({ defaultCurrency: 'EUR' });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.apiClient.isLoading$.pipe(takeUntil(this._unsubscribeAll), delay(0)).subscribe(loading => {
            if (loading) {
                this.gloabalLoader.show();
            } else {
                this.gloabalLoader.hide();
            }
        });

        this.apiClient.apiError
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap(data => this.notifyUserService.showError(data))
            )
            .subscribe();

        // Subscribe to config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
            this.fuseConfig = config;

            // Boxed
            if (this.fuseConfig.layout.width === 'boxed') {
                this.document.body.classList.add('boxed');
            } else {
                this.document.body.classList.remove('boxed');
            }

            // Color theme - Use normal for loop for IE11 compatibility
            for (let i = 0; i < this.document.body.classList.length; i++) {
                const className = this.document.body.classList[i];

                if (className.startsWith('theme-')) {
                    this.document.body.classList.remove(className);
                }
            }

            this.document.body.classList.add(this.fuseConfig.colorTheme);
        });

        this.authService.accountData$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter(aD => aD != null),
                tap(accountData => {
                    if (accountData.NeedsRefresh) {
                        this.authService.logout();
                    }
                })
            )
            .subscribe();

        const stored = sessionStorage.getItem('selected-forfettario');
        if (stored) {
            const selectedForfettario = JSON.parse(stored) as ForfettarioListItem;
            this.selezioneForfettarioService.setForfettarioCorrente(selectedForfettario).subscribe();
        }

        this.authService.authData$
            .pipe(
                takeUntil(this._unsubscribeAll),
                switchMap(authData => {
                    const helper = new JwtHelperService();

                    // Clear any previously scheduled expiry timer to avoid duplicates
                    if (this.tokenExpiryTimer) {
                        clearTimeout(this.tokenExpiryTimer);
                        this.tokenExpiryTimer = undefined;
                    }

                    if (authData && helper.isTokenExpired(authData.accessToken) === false) {
                        const minutoInMilli = 60000;

                        let alertTime = helper.getTokenExpirationDate(authData.accessToken).valueOf() - new Date().valueOf() - 5 * minutoInMilli;

                        // Only schedule the proactive refresh if the token expires MORE than 5
                        // minutes from now. A negative/zero alertTime (token expiring within 5 min)
                        // would make setTimeout fire immediately, creating a refresh loop.
                        // Short windows are handled reactively by the 401 refresh interceptor.
                        if (alertTime > 0 && alertTime < 2147483647) {
                            this.tokenExpiryTimer = setTimeout(() => this.authService.tokenIsExpiring(), alertTime);
                        }

                        this.authService.setDecodedToken(helper.decodeToken(authData.accessToken));

                        // Get default navigation
                        this.navigation = this.navigationService.get();

                        // Unregister the navigation to update the service
                        this._fuseNavigationService.unregister('main');

                        // Register the navigation to the service
                        this._fuseNavigationService.register('main', this.navigation);

                        // Set the main navigation as our current navigation
                        this._fuseNavigationService.setCurrentNavigation('main');

                        return this.authService.decodedToken$;
                    }

                    if (authData && helper.isTokenExpired(authData.accessToken) === true) {
                        // App booted with an expired access token: try a silent refresh
                        // before logging out. handleStartupSilentRefresh() navigates to
                        // login only if the refresh fails.
                        this.handleStartupSilentRefresh();
                        return of(null as DecodedJWTToken);
                    }

                    // No auth data at all: nothing to refresh. When a startup refresh is
                    // already in flight its own error handler performs the redirect, so we
                    // skip navigation here to avoid a duplicate one after logout().
                    if (this.isHandlingStartupRefresh === false) {
                        console.warn('Token expired or missing — navigating to login');
                        this.authService.setDecodedToken(null);
                        this.navigationService.navigatoToLogin();
                    }
                    return of(null as DecodedJWTToken);
                })
            )
            .subscribe({
                // In RxJS an error still terminates this subscription; this handler does not keep
                // it alive. It only prevents the uncaught global rethrow and resets the guard.
                // Synchronous-throw recovery is provided upstream by defer(...).
                error: (error: Error) => {
                    console.error('Unexpected error in authData$ pipeline:', error.message);
                    this.isHandlingStartupRefresh = false;
                },
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Cancel any pending proactive refresh timer so it cannot fire after destroy.
        if (this.tokenExpiryTimer) {
            clearTimeout(this.tokenExpiryTimer);
            this.tokenExpiryTimer = undefined;
        }

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Attempts a single silent token refresh when the app boots with an expired access token.
     * The re-entrancy guard makes concurrent authData$ emissions share one refresh attempt, and
     * the shared observable from AuthenticationService keeps SignalR refresh calls on the same
     * request. On success the refreshed token is emitted by the service; on failure the user is
     * redirected to login.
     */
    private handleStartupSilentRefresh(): void {
        if (this.isHandlingStartupRefresh) {
            return;
        }

        this.isHandlingStartupRefresh = true;

        // defer() makes the refresh invocation lazy so a synchronous throw inside
        // handleTokenRefresh() is delivered as an observable error and reaches the
        // catchError/finalize below instead of escaping the subscription.
        defer(() => this.authService.handleTokenRefresh())
            .pipe(
                takeUntil(this._unsubscribeAll),
                catchError(() => {
                    console.warn('Startup silent refresh failed — navigating to login');
                    this.navigationService.navigatoToLogin();
                    return of(null as string);
                }),
                finalize(() => {
                    this.isHandlingStartupRefresh = false;
                })
            )
            .subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
}
